import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Lock, User, Eye, EyeOff, Moon, Sun } from 'lucide-react';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login } = useAuth();
    const { isDark, toggleDarkMode } = useDarkMode();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setLoading(true);

		try{
			const success = await login(username, password);
			if(success){
				navigate('/dashboard');
			}else{
				setError('Username or password is wrong.');
			}
		}catch(err){
			setError('An error occurred. Please try again later.');
		}finally{
			setLoading(false);
		}
    };

	return (
		<div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-[#0a0a0a]' : 'bg-neutral-50'} overflow-hidden relative`}>
			{/* FUI Decorative background elements */}
			<div className="absolute inset-0 pointer-events-none overflow-hidden">
				<div className={`absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full opacity-10 blur-[120px] ${isDark ? 'bg-emerald-500' : 'bg-emerald-400'}`} />
				<div className={`absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full opacity-10 blur-[120px] ${isDark ? 'bg-cyan-500' : 'bg-cyan-400'}`} />
				{/* Scanning line animation */}
				<div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.2) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
			</div>

			{/* Dark Mode Toggle */}
			<button 
				onClick={toggleDarkMode} 
				className={`absolute top-6 right-6 p-2.5 rounded-none border transition-all duration-300 z-50 ${
					isDark 
						? 'bg-[#111] border-[#333] hover:border-emerald-500 text-emerald-500 hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]' 
						: 'bg-white border-neutral-200 hover:border-emerald-600 text-emerald-700 hover:shadow-[0_0_10px_rgba(16,185,129,0.2)]'
				}`}
			>
				{isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
			</button>

			<div className="w-full max-w-md relative z-10">
				{/* Technical Status Display */}
				<div className={`flex justify-between items-end mb-1 px-1 font-mono text-[9px] uppercase tracking-[0.2em] ${isDark ? 'text-emerald-500/60' : 'text-emerald-700/60'}`}>
					<div className="flex items-center gap-2">
						<span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
						<span>AUTH_GATEWAY_v2.0</span>
					</div>
					<span>ENCRYPTION: AES-256</span>
				</div>

				<div className={`p-8 rounded-none border-t-2 ${
					isDark 
						? 'bg-[#111] border-x border-b border-[#222] border-t-emerald-500 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' 
						: 'bg-white border-x border-b border-neutral-200 border-t-emerald-600 shadow-[0_10px_30px_rgba(0,0,0,0.05)]'
				}`}>
					<div className="text-center mb-10">
						<h1 className={`text-2xl font-mono font-bold uppercase tracking-[0.3em] mb-2 ${isDark ? 'text-white' : 'text-black'}`}>
							MANAGER
						</h1>
						<div className="flex items-center justify-center gap-2 overflow-hidden">
							<div className={`h-[1px] flex-1 ${isDark ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
							<p className={`text-[10px] font-mono uppercase tracking-[0.2em] px-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>SECURE_ACCESS_PROMPT</p>
							<div className={`h-[1px] flex-1 ${isDark ? 'bg-neutral-800' : 'bg-neutral-200'}`} />
						</div>
					</div>

					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="group">
							<label htmlFor="username" className={`block text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
								[01] USER_IDENTIFIER
							</label>
							<div className="relative">
								<div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${isDark ? 'bg-neutral-800 group-focus-within:bg-emerald-500' : 'bg-neutral-200 group-focus-within:bg-emerald-600'}`} />
								<User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${isDark ? 'text-neutral-600 group-focus-within:text-emerald-500' : 'text-neutral-400 group-focus-within:text-emerald-600'}`} />
								<input 
									id="username" 
									type="text" 
									value={username} 
									onChange={(e) => setUsername(e.target.value)} 
									className={`w-full pl-12 pr-4 py-3 text-xs font-mono uppercase tracking-wider rounded-none focus:outline-none transition-all ${
										isDark 
											? 'bg-[#0d0d0d] border border-[#222] text-neutral-200 focus:border-emerald-500/50 placeholder-neutral-800' 
											: 'bg-neutral-50 border border-neutral-200 text-neutral-800 focus:border-emerald-600/50 placeholder-neutral-300'
									}`}
									placeholder="INPUT_USERNAME..." 
									required 
								/>
							</div>
						</div>

						<div className="group">
							<label htmlFor="password" className={`block text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
								[02] ACCESS_KEY_PHRASE
							</label>
							<div className="relative">
								<div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${isDark ? 'bg-neutral-800 group-focus-within:bg-emerald-500' : 'bg-neutral-200 group-focus-within:bg-emerald-600'}`} />
								<Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${isDark ? 'text-neutral-600 group-focus-within:text-emerald-500' : 'text-neutral-400 group-focus-within:text-emerald-600'}`} />
								<input 
									id="password" 
									type={showPassword ? 'text' : 'password'} 
									value={password} 
									onChange={(e) => setPassword(e.target.value)} 
									className={`w-full pl-12 pr-12 py-3 text-xs font-mono uppercase tracking-wider rounded-none focus:outline-none transition-all ${
										isDark 
											? 'bg-[#0d0d0d] border border-[#222] text-neutral-200 focus:border-emerald-500/50 placeholder-neutral-800' 
											: 'bg-neutral-50 border border-neutral-200 text-neutral-800 focus:border-emerald-600/50 placeholder-neutral-300'
									}`}
									placeholder="INPUT_PASSWORD..." 
									required 
								/>
								<button 
									type="button" 
									onClick={() => setShowPassword(!showPassword)} 
									className={`absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-500' : 'text-neutral-400 hover:text-emerald-600'}`}
								>
									{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
						</div>

						{error && (
							<div className={`border-l-2 py-3 px-4 flex items-center gap-3 animate-pulse ${
								isDark ? 'bg-red-500/5 border-red-500/50 text-red-400' : 'bg-red-50 border-red-600/50 text-red-700'
							}`}>
								<div className="w-1.5 h-1.5 rounded-full bg-current" />
								<p className="text-[10px] font-mono uppercase tracking-widest">{error}</p>
							</div>
						)}

						<button 
							type="submit" 
							disabled={loading} 
							className={`w-full group relative overflow-hidden flex items-center justify-center px-4 py-3 rounded-none font-mono text-xs uppercase tracking-[0.25em] transition-all duration-300 ${
								isDark 
									? 'bg-[#1a1a1a] border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500 hover:text-emerald-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.2)]' 
									: 'bg-white border border-emerald-600/50 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 hover:text-emerald-800 hover:shadow-[0_0_15px_rgba(16,185,129,0.1)]'
							}`}
						>
							<div className={`absolute inset-0 w-0 group-hover:w-full transition-all duration-500 opacity-10 bg-gradient-to-r from-transparent via-emerald-500 to-transparent`} />
							{loading ? (
								<div className={`w-4 h-4 border-2 rounded-full animate-spin ${isDark ? 'border-emerald-400 border-t-transparent' : 'border-emerald-600 border-t-transparent'}`} />
							) : (
								<span className="relative z-10">INITIATE_SESSION</span>
							)}
						</button>
					</form>

					{/* Technical Footer Decoration */}
					<div className={`mt-10 pt-4 border-t border-dashed flex justify-between items-center ${isDark ? 'border-neutral-800 text-neutral-600' : 'border-neutral-200 text-neutral-400'}`}>
						<div className="flex gap-1">
							<div className="w-1 h-1 bg-current opacity-30" />
							<div className="w-1 h-1 bg-current opacity-30" />
							<div className="w-1 h-1 bg-current opacity-30" />
						</div>
						<span className="text-[8px] font-mono tracking-[0.3em]">SECURE_STORAGE_v4.4.1</span>
						<div className="flex gap-1">
							<div className="w-1 h-1 bg-current opacity-30" />
							<div className="w-1 h-1 bg-current opacity-30" />
							<div className="w-1 h-1 bg-current opacity-30" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Login;
